import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { DropdownMappingMoreActionsOptionType } from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-more-actions';
import type { CentralMappingCategory } from 'lumen/models';
import { USER_ENGAGEMENT_ELEMENT_IDS } from 'lumen/models/seen-user-engagement-element';
import type HotelSettingsCentralMappingHierarchyRoute from 'lumen/routes/hotel/settings/central-mapping/hierarchy';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  type Breakdown,
  BREAKDOWN_TRANSLATIONS,
  CENTRAL_MAPPING_HELP_CENTER_LINKS,
  CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID,
  DEFAULT_BREAKDOWN,
  type MappingManagedByType,
} from 'lumen/utils/central-mapping-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export const TrackHierarchyNumberToLevel = {
  1: 'group',
  2: 'category',
  3: 'sub-category',
} as const;

export type TrackHierarchyLevelOptions =
  keyof typeof TrackHierarchyNumberToLevel;

export type TrackHierarchyActions = 'add' | 'edit' | 'delete';

export default class HotelSettingsCentralMappingHierarchyController extends Controller {
  declare model: ModelFrom<HotelSettingsCentralMappingHierarchyRoute>;
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare centralMapping: Services['central-mapping'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare userEngagement: Services['user-engagement'];

  @tracked breakdown: Breakdown = DEFAULT_BREAKDOWN;
  @tracked categoryToDelete: CentralMappingCategory | null = null;

  // TODO: CEMA - To be removed after internal testing needs.
  @tracked managedBy: MappingManagedByType | null = null;

  @tracked selectedCategories: CentralMappingCategory[] = [];
  @tracked showConfirmationModalDeleteCategory = false;

  MAX_DEPTH = 2;

  updateHierarchyTask = task({ restartable: true }, async (): Promise<void> => {
    const { flattenedHierarchy, breakdown } = this;

    const rootCategoriesIds = this.rootCategories.map(
      (category) => category.id,
    );

    const serializedCategories = flattenedHierarchy
      .filter((category) => !category.isDeleted)
      .map((category) =>
        category.serialize({
          includeId: true,
          // Add isRootCategory to the serialization options if the category is a root category.
          // This is used to remove the parent_id assigned to non-fixed root categories in the get task.
          ...(rootCategoriesIds.includes(category.id) && {
            isRootCategory: true,
          }),
        }),
      );

    const payload = {
      meta: { breakdown },
      data: serializedCategories,
    };

    const params: Record<string, string> = {
      subscription_id: this.hotelAndMarketService.hotel.id!,
      breakdown,
    };
    if (this.managedBy != null) {
      params['managed_by'] = this.managedBy;
    }

    try {
      const url = formatUrl(
        '/apigateway/v1/app/mapping/categories',
        params,
        this.ajax.host,
      );

      await this.ajax.post(url, { data: payload });

      flattenedHierarchy
        .filter((category) => category.isDeleted)
        .forEach((category) => {
          category.unloadRecord();
        });

      void this.model.hierarchy.perform({
        breakdown,
        managedBy: this.managedBy ?? undefined,
      });
      flattenedHierarchy.forEach((item) => item.updateRelationships());

      this.toast.success(
        this.intl.t('settings.central_mapping.hierarchy.toast.success'),
        {
          showIcon: true,
        },
      );

      this.usageAnalytics.track('Hierarchy Updated', {
        eventTrigger: 'save-button',
        breakdown,
      });
    } catch (error) {
      this.errorTracking.captureException(error);

      // Show retry alert in a different task, otherwise the save button will stay in a loading state while showing the alert.
      void this.centralMapping.showRetryAlertTask.perform(
        this.updateHierarchyTask,
      );
    }
  });

  get flattenedHierarchy(): CentralMappingCategory[] {
    const flattenCategory = (
      category: CentralMappingCategory,
    ): CentralMappingCategory[] => {
      const { children } = category;
      return [category, ...children.slice().flatMap(flattenCategory)];
    };

    const flattenedHierarchyWithDuplicates = this.hierarchy
      .flatMap(flattenCategory)
      .filter((category) => category.depth !== -1);

    const uniqueFlattenedHierarchy = new Set(flattenedHierarchyWithDuplicates);
    return Array.from(uniqueFlattenedHierarchy);
  }

  get hasChanges(): boolean {
    return this.flattenedHierarchy.some(
      (category) =>
        category.hasDirtyAttributes ||
        category.hasDirtyRelationships ||
        category.isDeleted,
    );
  }

  get hasMarketAccess(): boolean {
    return (
      this.access.hasCentralMappingCategoriesMarketViewAccess ||
      this.access.hasCentralMappingCategoriesMarketEditAccess
    );
  }

  get hasMarketEditAccess(): boolean {
    return this.access.hasCentralMappingCategoriesMarketEditAccess;
  }

  get hierarchy(): CentralMappingCategory[] {
    return this.model.hierarchy.last?.value?.hierarchy ?? [];
  }

  get hierarchyPageBadgeText(): string {
    if (this.isHierarchyManagedByMarket) {
      return this.intl.t(
        'settings.central_mapping.read_only.portfolio_managed',
      );
    }
    return this.intl.t('settings.central_mapping.read_only.view_only');
  }

  get isHierarchyManagedByMarket(): boolean {
    return (
      this.hasMarketAccess && !!this.hotelAndMarketService.hotel.spiderMarketId
    );
  }

  get isLoading(): boolean {
    return this.model.hierarchy.last?.isRunning ?? false;
  }

  get isReadOnly(): boolean {
    // Portfolio-managed hotels (those belonging to a spider market) have a
    // strictly read-only hierarchy at the hotel level; editing happens at the
    // portfolio (market) level only.
    if (this.hotelAndMarketService.hotel.spiderMarketId) {
      return true;
    }
    return !this.access.hasCentralMappingCategoriesEditAccess;
  }

  get isSaveButtonEnabled(): boolean {
    const isCategoryBeingAdded = this.flattenedHierarchy.some(
      (category) => !category.hasConfirmedName,
    );

    // When a category is new, don't allow saving until a name was confirmed.
    if (isCategoryBeingAdded) {
      return false;
    }

    return this.hasChanges;
  }

  get moreActionsOptions(): DropdownMappingMoreActionsOptionType[] {
    const options: DropdownMappingMoreActionsOptionType[] = [
      {
        id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.HELP_CENTER,
        label: this.intl.t('general.help_center'),
        icon: 'book',
        unsafe_isLink: true,
      },
    ];

    if (!this.isReadOnly && !this.isHierarchyManagedByMarket) {
      options.unshift({
        id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.NAVIGATE_TO_MAPPING,
        label: this.navigateToMappingButtonLabel,
        icon: this.isReadOnly ? undefined : 'edit-pencil',
      });
    }

    return options;
  }

  get navigateToMappingButtonLabel(): string {
    if (this.isReadOnly) {
      return this.intl.t('settings.central_mapping.menu.view_mapping');
    }

    return this.intl.t('settings.central_mapping.menu.map_values');
  }

  get navigateToMarketButtonLabel(): string {
    if (this.hasMarketEditAccess) {
      return this.intl.t('settings.central_mapping.navigate_to_market.edit');
    }
    return this.intl.t('settings.central_mapping.navigate_to_market.view');
  }

  get rootCategories(): CentralMappingCategory[] {
    return this.flattenedHierarchy.filter(
      (category) => category.depth === 0 && !category.isDeleted,
    );
  }

  get showHierarchyPageBadge(): boolean {
    return this.isReadOnly && !this.model.hierarchy?.last?.isRunning;
  }

  get showUserOnboardingModal(): boolean {
    return (
      !this.isLoading &&
      !this.isReadOnly &&
      !this.userEngagement.hasSeen(
        USER_ENGAGEMENT_ELEMENT_IDS.CENTRAL_MAPPING_HIERARCHY_ONBOARDING_MODAL,
      )
    );
  }

  get title(): string {
    const translation = BREAKDOWN_TRANSLATIONS[this.breakdown];
    return translation
      ? `${this.intl.t(translation)} ${this.intl.t('settings.central-mapping.hierarchy')}`
      : '';
  }

  trackHierarchy(
    action: TrackHierarchyActions,
    level: TrackHierarchyLevelOptions,
  ): void {
    this.usageAnalytics.track('Hierarchy Changed', {
      eventTrigger: 'change-hierarchy',
      action,
      level: TrackHierarchyNumberToLevel[level],
    });
  }

  @action closeConfirmationModalDeleteCategory(): void {
    this.showConfirmationModalDeleteCategory = false;
  }

  @action closeUserOnboardingModal(): void {
    this.userEngagement.markAsSeen(
      USER_ENGAGEMENT_ELEMENT_IDS.CENTRAL_MAPPING_HIERARCHY_ONBOARDING_MODAL,
    );
  }

  @action deleteCategory(): void {
    const { categoryToDelete } = this;

    if (!categoryToDelete) {
      return;
    }

    // Store depth value before calling `this.deleteCategoryAndChildren`, otherwise `categoryToDelete.hasChildren` is not accurate anymore.
    const categoryDepth = categoryToDelete.depth + 1;

    this.deleteCategoryAndChildren(categoryToDelete);
    this.showConfirmationModalDeleteCategory = false;
    this.trackCategoryDeleted(categoryDepth);
  }

  @action deleteCategoryAndChildren(category: CentralMappingCategory): void {
    category.undeletedChildren.forEach((child) => {
      this.deleteCategoryAndChildren(child);
    });

    // Also unselect the category if it's selected.
    if (this.selectedCategories.includes(category)) {
      this.selectCategory(category);
    }

    category.deleteRecord();
  }

  @action deleteCategoryWithConfirmationModal(
    category: CentralMappingCategory,
  ): void {
    this.categoryToDelete = category;
    this.showConfirmationModalDeleteCategory = true;
  }

  @action
  discardChanges(): void {
    this.flattenedHierarchy.forEach((category) => {
      category.rollbackAttributes();
      category.rollbackRelationships();
    });
  }

  @action
  async onClickDiscardChanges(): Promise<void> {
    if (this.hasChanges) {
      await this.centralMapping.showUnsavedChangesAlert(() =>
        this.discardChanges(),
      );
    }
  }

  @action onClickMapValues(): void {
    this.router.transitionTo('hotel.settings.central-mapping.mapping', {
      queryParams: { breakdown: this.breakdown },
    });

    this.usageAnalytics.track('Mapping Page Switched', {
      eventTrigger: 'switch-mapping',
    });
  }

  @action
  onClickNavigateToMarket(): void {
    const marketId = this.hotelAndMarketService.hotel.spiderMarketId!;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'market.settings.central-mapping.hierarchy',
      marketId,
      {
        queryParams: { breakdown: this.breakdown },
      },
    );
  }

  @action
  onClickSave(): void {
    void this.updateHierarchyTask.perform();
  }

  @action onSelectMoreActionsOption(
    option: DropdownMappingMoreActionsOptionType,
  ): void {
    switch (option.id) {
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.NAVIGATE_TO_MAPPING:
        this.onClickMapValues();
        break;
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.HELP_CENTER:
        this.openHelpArticle();
        break;
      default:
        break;
    }
  }

  @action openHelpArticle(): void {
    this.intercom.redirectToIntercomHelp(
      CENTRAL_MAPPING_HELP_CENTER_LINKS.HIERARCHY_PAGE,
    );
  }

  @action
  selectCategory(category: CentralMappingCategory): void {
    const { selectedCategories } = this;
    const { depth } = category;

    // Replace the whole array to trigger a re-render.
    const newSelectedCategories = [...selectedCategories];

    const trimArray = (
      array: CentralMappingCategory[],
      newSize: number,
    ): void => {
      array.length = newSize;
    };
    const setCategoryAtDepth = (
      array: CentralMappingCategory[],
      cat: CentralMappingCategory,
    ): void => {
      array[cat.depth] = cat;
    };

    const lastSelectedCategory =
      selectedCategories[selectedCategories.length - 1];
    const isSiblingOfLastSelectedCategory =
      lastSelectedCategory?.parent?.undeletedChildren.includes(category) ??
      false;
    const isParentOfLastSelectedCategory = lastSelectedCategory
      ? newSelectedCategories.includes(lastSelectedCategory)
      : false;
    const isLastSelectedCategory = lastSelectedCategory === category;

    if (isLastSelectedCategory) {
      // Unselect the last category.
      trimArray(newSelectedCategories, depth);
    } else if (isSiblingOfLastSelectedCategory) {
      // Unselect category and all of its children.
      trimArray(newSelectedCategories, depth);
      setCategoryAtDepth(newSelectedCategories, category);
    } else if (isParentOfLastSelectedCategory) {
      // Select category and unselect all of its children.
      trimArray(newSelectedCategories, depth + 1);
      setCategoryAtDepth(newSelectedCategories, category);
    } else {
      setCategoryAtDepth(newSelectedCategories, category);
    }

    this.selectedCategories = newSelectedCategories;
  }

  @action trackCategoryAdded(level: number): void {
    this.trackHierarchy('add', level as TrackHierarchyLevelOptions);
  }

  @action trackCategoryDeleted(level: number): void {
    this.trackHierarchy('delete', level as TrackHierarchyLevelOptions);
  }

  @action trackCategoryRenamed(level: number): void {
    this.trackHierarchy('edit', level as TrackHierarchyLevelOptions);
  }
}
